import React, { useEffect, useState } from 'react';
import { Button, Icon, Modal, Title } from '@gerdau/hefesto';
import { DateField } from '@gerdau/hefesto-mais-web/DateField';
import { DateType } from '@gerdau/hefesto-mais-web/DateField/DateField.types';
import alasql from 'alasql';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import theme from '../../../../assets/styles/theme/theme';
import { IDuplicatesByDayState } from '../../../../store/ducks/dashboard/types';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { DashboardGraphHeader, DashboardItemContainer } from '../../styles';
import DuplicatesByDayChart from './DuplicatesByDayChart';
import DuplicatesByDayTable from './DuplicatesByDayTable';

export interface IDuplicatesByDayData {
  date: string;
  duplicates: IDuplicate[];
}
interface IDuplicate {
  status: string;
  totalInvoice: number;
}

interface TotalRow {
  Status: string;
  Total: number;
}

interface IDuplicatesByDayProps {
  duplicatesByDayState: IDuplicatesByDayState;
  errorMessageBlock: React.ReactNode;
  onChangeMonth(selectedMonth: moment.Moment): void;
}

const DuplicatesByDay: React.FC<IDuplicatesByDayProps> = ({
  duplicatesByDayState,
  errorMessageBlock,
  onChangeMonth,
  children
}) => {
  const title = 'Duplicatas por dia de vencimento (R$)';
  const [canShowModalTable, setCanShowModalTable] = useState(false);
  const [allDescriptions, setAllDescriptions] = useState<string[]>([]);
  const dispatch = useDispatch();

  useEffect(() => {
    const getAllProductsDescription = (): string[] => {
      const descriptions: string[] = [];
      duplicatesByDayState.data.forEach((item: IDuplicatesByDayData) => {
        item.duplicates.forEach((duplicate: IDuplicate) => {
          if (!descriptions.includes(duplicate.status)) {
            descriptions.push(duplicate.status);
          }
        });
      });
      descriptions.sort((a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0));
      return descriptions;
    };

    setAllDescriptions(getAllProductsDescription());
  }, [duplicatesByDayState.data]);

  const isEmptyData = (): boolean => {
    return !duplicatesByDayState.data.some(
      (item: IDuplicatesByDayData) => item.duplicates.length > 0
    );
  };

  const generateDuplicatesByDayExcel = () => {
    const dataToExcel: TotalRow[] = [];
    const chartData = duplicatesByDayState.data;

    let totalsRow: TotalRow = {
      Status: 'Total diário',
      Total: chartData.reduce(
        (acc: number, curr: IDuplicatesByDayData) =>
          curr.duplicates.reduce(
            (accDuplicatesSum: number, currDuplicate: IDuplicate) =>
              currDuplicate.totalInvoice + accDuplicatesSum,
            0
          ) + acc,
        0
      )
    };
    chartData.forEach((item: IDuplicatesByDayData) => {
      totalsRow = {
        ...totalsRow,
        [item.date]: item.duplicates.reduce(
          (accDuplicatesSum: number, currDuplicate: IDuplicate) =>
            currDuplicate.totalInvoice + accDuplicatesSum,
          0
        )
      };
    });
    dataToExcel.push(totalsRow);

    allDescriptions.forEach((duplicateStatus: string) => {
      let row: TotalRow = {
        Status: duplicateStatus,
        Total: chartData.reduce(
          (acc: number, curr: IDuplicatesByDayData) =>
            acc +
            (curr.duplicates.find(
              (i: IDuplicate) => i.status === duplicateStatus
            )?.totalInvoice || 0),
          0
        )
      };

      chartData.forEach((item: IDuplicatesByDayData) => {
        row = {
          ...row,
          [item.date]:
            item.duplicates.find(
              (i: IDuplicate) => i.status === duplicateStatus
            )?.totalInvoice || 0
        };
      });

      dataToExcel.push(row);
    });

    const opts = {
      headers: true,
      column: {
        style: {
          width: 200,
          Font: { Bold: '1' },
          Interior: { Color: theme.colors.waterBlue, Pattern: 'Solid' },
          Alignment: { Horizontal: 'Right' }
        }
      },
      row: {
        style: {
          Font: { Color: theme.colors.black },
          Alignment: { Horizontal: 'Right' }
        }
      },
      columns: [
        { width: 100, height: 100, columnid: 'Status' },
        { width: 100, height: 100, columnid: 'Total' }
      ]
    };

    chartData.forEach((item: IDuplicatesByDayData) => {
      opts.columns.push({
        width: 100,
        height: 100,
        columnid: item.date
      });
    });

    const dataMonth = moment(chartData[0].date, 'DD/MM').format('MMMM');
    const fileName = `duplicatas_${dataMonth}`;

    alasql('SELECT * INTO XLSXML("' + fileName + '.xls",?) FROM ?', [
      opts,
      dataToExcel
    ]);
  };

  const prepareAndSendTagging = (itemName: string, itemDescription: string) => {
    dispatch(
      sendTagging({
        screenName: 'LG:PainelGestao:Selecao',
        itemName: itemName,
        itemDescription: itemDescription,
        event: 'click'
      })
    );
  };

  return (
    <DashboardItemContainer>
      <DashboardGraphHeader>
        <div className="data-picker">
          <DateField
            label={''}
            name={'Datepicker'}
            onChange={(newDate: Date | [DateType, DateType] | null) =>
              onChangeMonth(moment(newDate as Date))
            }
            picker="month"
            selected={duplicatesByDayState.monthDate.toDate()}
          />
        </div>
        <Title level={4}>{title}</Title>
        <div className="actions__container">
          {!duplicatesByDayState.error && !isEmptyData() && (
            <div className="actions">
              <Button
                name={'Baixar'}
                onClick={() => {
                  generateDuplicatesByDayExcel();
                  prepareAndSendTagging(
                    'button:baixar',
                    'painelGestao_duplicatasPorDiaDeVencimento_baixar'
                  );
                }}
                variant="secondary"
                icon={<Icon option="Download" />}
                loading={duplicatesByDayState.loading}
              >
                Baixar
              </Button>
              <Button
                variant="secondary"
                name={'Ver como tabela'}
                onClick={() => {
                  setCanShowModalTable(true);
                  prepareAndSendTagging(
                    'button:verComoTabela',
                    'painelGestao_duplicatasPorDiaDeVencimento_verComoTabela'
                  );
                }}
                loading={duplicatesByDayState.loading}
              >
                Ver como tabela
              </Button>
            </div>
          )}
        </div>
      </DashboardGraphHeader>

      {duplicatesByDayState.error && !duplicatesByDayState.loading && (
        <>{errorMessageBlock}</>
      )}

      {duplicatesByDayState.loading && <>{children}</>}

      {!duplicatesByDayState.error && !duplicatesByDayState.loading && (
        <>
          <DuplicatesByDayChart
            data={duplicatesByDayState.data}
            isEmpty={isEmptyData()}
            allDescriptions={allDescriptions}
          />
          {canShowModalTable && (
            <Modal
              title={title}
              buttons={{
                right: {
                  children: 'Baixar tabela',
                  name: 'baixar-tabela',
                  onClick: () => {
                    generateDuplicatesByDayExcel();
                    prepareAndSendTagging(
                      'button:baixar',
                      'painelGestao_duplicatasPorDiaDeVencimento_baixar'
                    );
                  },
                  type: 'button'
                }
              }}
              onClose={() => setCanShowModalTable(false)}
            >
              <DuplicatesByDayTable
                data={duplicatesByDayState.data}
                allProductsDescription={allDescriptions}
              />
            </Modal>
          )}
        </>
      )}
    </DashboardItemContainer>
  );
};

export default DuplicatesByDay;
